import { useCallback, useEffect, useRef, useState } from "react";
import {
  getEntity,
  getEntityApprovalMode,
  getEntityAttention,
  listEntityPermissions,
  setEntityApprovalMode,
  setEntityAttention,
  type AttentionSignal,
  type EntityApprovalMode,
  type EntityAttention,
  type EntityPermissionRow,
  type EntityData,
} from "@/services/api/entityApi";
import { getGoogleIntegrationStatus, type GoogleIntegrationStatus } from "@/services/api/googleIntegrationApi";
import { getLinkedInIntegrationStatus, type LinkedInIntegrationStatus } from "@/services/api/linkedinIntegrationApi";
import { getMySubscription, type MySubscription } from "@/services/api/monetizationApi";
import { getCallHistory } from "@/services/api/kernelBotApi";
import { getRaiseProfile, type RaiseProfile } from "@/services/api/investorOutreachApi";
import { getPacing, getPortfolio, updatePacing, type HarnessPacing, type PacingPreset } from "@/services/api/harnessApi";
import { listMailMailboxes, type MailMailbox } from "@/services/api/mailboxApi";
import { getWhatsAppStatus, type WhatsAppStatusResponse } from "@/services/api/whatsappIntegrationApi";
import { firebaseReady, getCurrentEntityId, getUserEntityId, useCurrentEntityId } from "@/providers/firebase/authProvider";

/**
 * THE TEN SECTIONS' VALUES, READ ONCE (P206 T0202).
 *
 * WHY ONE HOOK AND NOT A FETCH PER SECTION. The page is a list of ten answers; a per-section fetch
 * would fire nine requests to paint one screen and every section would invent its own loading and
 * failure handling. More importantly, **the honest states must be distinguishable**: "Raisy could
 * not read this now", "this does not exist yet" and "the value really is zero" are three different
 * answers, and a page that renders all three as a blank cell is the defect this project exists to
 * fix (FTUE implication 13 · the PO's own *"why do I see nothing"*).
 *
 * So every read is modelled as a THREE-STATE result and the page is forbidden from collapsing
 * them:
 *   - `loading`      — asked, not answered yet;
 *   - `ready`        — answered, and the value is real (an EMPTY LIST IS A REAL ANSWER);
 *   - `unavailable`  — the read failed or the home does not exist, WITH its reason.
 *
 * ONE FAILURE NEVER BLANKS THE PAGE: `Promise.allSettled` semantics are built in by giving each
 * read its own state, so a single unreachable endpoint costs one section its answer and nothing
 * else. The page shows the other nine.
 *
 * ⚠️ THE READS ARE DELIBERATELY BOUND TO EXISTING ENDPOINTS, each read in the repo before being
 * called from here. Where a section's STORE does not exist yet, this hook returns an honest
 * `unavailable` with the reason — it does not call an endpoint that would 404 and it does not let
 * the section render a control that saves nothing (D-206-001).
 */
export type ReadState<T> =
  | { state: "loading" }
  | { state: "ready"; value: T }
  | { state: "unavailable"; reason: string };

function reasonOf(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "the answer did not arrive";
}

/**
 * Open the API envelope, and treat a MISSING body as a FAILED READ.
 *
 * `ApiResponse<T>` declares `data: T | null`, so `.then((r) => r.data)` hands back a nullable value —
 * which is both a type error against every `ReadState<T>` here and, more importantly, the wrong
 * MEANING: a null body would flow into a section as a real value and be rendered as `0`, `false` or
 * an empty list. "Raisy answered with nothing" is a failure to read, and it is reported as one.
 */
function unwrap<T>(r: { data?: T | null }): T {
  if (r?.data === undefined || r?.data === null) {
    throw new Error("the answer arrived without a value");
  }
  return r.data;
}

/**
 * ⚠️ A WRITE THE STORE REFUSED MUST NOT RESOLVE (P206 T0400 — found in the security pass).
 *
 * The three saving controls reported "Saved" whenever their promise RESOLVED, and resolution proves
 * nothing here: the API wrappers (`entityApi.apiCall`, `harnessApi.apiCall`) return `res.json()`
 * without looking at `res.ok` or at the envelope's `status`, so a refusal arrives as an ordinary
 * return value. Both shapes are real:
 *
 *   · **HTTP 200 with `status:false`** — `/harness/pacing` answers exactly this for an entity the
 *     caller cannot write (`{"status":false,"statusCode":404,"message":"Entity not found"}`).
 *   · **HTTP 404/400 with `status:false` and `data:{}`** — `/entity/{id}/approval-mode` and
 *     `/entity/{id}/attention`.
 *
 * `unwrap()` is deliberately NOT reused for writes: it tests `data`, and `data:{}` passes it. The
 * envelope's own `status` is the machine-readable verdict, so that is what a write is judged on —
 * otherwise the row tells a person his cadence changed when it did not, which is the one thing
 * D-206-001 forbids this surface to do.
 */
function mustSucceed<T>(r: { status?: boolean; statusCode?: number; message?: string; data?: T | null }): T {
  if (r?.status !== true) {
    throw new Error(r?.message || `the store refused this (code ${r?.statusCode ?? "unstated"})`);
  }
  return r.data as T;
}

/** One read, keyed by what it depends on (the company it belongs to). Re-runnable on demand. */
function useAsyncRead<T>(
  key: string | null,
  load: (key: string) => Promise<T>,
): { read: ReadState<T>; reload: () => void } {
  const [nonce, setNonce] = useState(0);
  const [read, setRead] = useState<ReadState<T>>({ state: "loading" });
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!key) {
      setRead({ state: "unavailable", reason: "no company is chosen yet" });
      return;
    }
    let live = true;
    setRead({ state: "loading" });
    loadRef.current(key)
      .then((value) => {
        if (live) setRead({ state: "ready", value });
      })
      .catch((err) => {
        if (live) setRead({ state: "unavailable", reason: reasonOf(err) });
      });
    return () => {
      live = false;
    };
  }, [key, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { read, reload };
}

/** The two facts a section needs while a write is in flight, so no control ever lies about saving. */
export type SaveState = "idle" | "saving" | "saved" | "failed";

export interface UserSettings {
  /** The company these settings are about — the entity in the rail. Never the person's own entity. */
  companyEntityId: string | null;
  approvalMode: ReadState<EntityApprovalMode>;
  pacing: ReadState<HarnessPacing>;
  mailboxes: ReadState<MailMailbox[]>;
  google: ReadState<GoogleIntegrationStatus>;
  linkedin: ReadState<LinkedInIntegrationStatus>;
  whatsapp: ReadState<WhatsAppStatusResponse>;
  /** Row 5's COUNTS — what is waiting for him right now, from `/harness/portfolio`. */
  needsYou: ReadState<{ process_gate: number; hermes_wake: number; preparation: number | null }>;
  /** Row 5's PREFERENCE — which of those counts may reach him (his own, per person). */
  attentionPreference: ReadState<EntityAttention>;
  permissions: ReadState<EntityPermissionRow[]>;
  /** The person's own entity — his profile (row 7) and his declared round (row 10) both live here. */
  person: ReadState<EntityData>;
  /** Row 10 — read from the raise profile's OWN route, not a second reading of the entity bag. */
  round: ReadState<RaiseProfile>;
  /** Row 9's RECEIPTS — how many calls are on the record for this company (P206 T0304). */
  receipts: ReadState<number>;
  plan: ReadState<MySubscription>;
  saveApprovalMode: (mode: EntityApprovalMode) => Promise<SaveState>;
  savePacing: (preset: PacingPreset) => Promise<SaveState>;
  saveAttention: (signals: AttentionSignal[]) => Promise<SaveState>;
  approvalSave: SaveState;
  approvalSaveError: string | null;
  pacingSave: SaveState;
  pacingSaveError: string | null;
  attentionSave: SaveState;
  attentionSaveError: string | null;
}

export function useUserSettings(): UserSettings {
  /**
   * WHICH ENTITY THIS PAGE IS ABOUT — the two ids are NOT interchangeable, and mixing them up
   * would show a person another company's round with no visible sign that it happened.
   *
   *   - `companyEntityId` — the company in the rail. A round, a cadence, an approval mode and a
   *     team are all facts ABOUT A COMPANY: the round is what that company is raising, the cadence
   *     is how often Raisy works on it, and the people with access are its people. These key on it.
   *   - `resolvedId` — the person's OWN entity. Only his profile row is about him rather than about a
   *     company, and it is the one id that must survive a cold page load (see below).
   *
   * The fallback is deliberate: with nothing chosen in the rail, the person's own entity is the one
   * context that certainly exists, and reading it is better than refusing to answer — the page
   * states whose settings these are either way.
   */
  const railEntityId = useCurrentEntityId();

  /**
   * ⚠️ THE COOKIE IS NOT THERE YET ON A COLD NAVIGATION — AND A ONE-TIME READ NEVER RECOVERS.
   *
   * This was `useMemo(() => getUserEntityId(), [])`, i.e. resolved ONCE at the first render. On a
   * direct visit to `/settings` (a bookmark, a refresh, a deep link) the Firebase auth state has not
   * settled, so the stored-user cookie is still empty — and the value stayed `null` for the life of
   * the mount. Six rows then said *"no company is chosen yet"* while the page, the rail and the
   * data were all perfectly fine. **Observed, not theorised** (measured 2026-09-22 against the
   * deployed DEV FE: rows 1, 2 and 3 rendered that sentence on a cold load and correct values after
   * a warm one).
   *
   * The honest-empty design is what made it visible — the row said WHY rather than showing a zero.
   * The fix is two-part: the rail's reactive store is read reactively (it always was), and the
   * fallback id is **re-read once auth has actually resolved** (`firebaseReady`), so a page opened
   * cold ends up with the company it should have had from the start.
   */
  const [resolvedId, setResolvedId] = useState<string | null>(() => getCurrentEntityId() ?? getUserEntityId());
  useEffect(() => {
    let live = true;
    firebaseReady
      .then(() => { if (live) setResolvedId(getCurrentEntityId() ?? getUserEntityId()); })
      .catch(() => { /* a failed auth resolution is not this page's error to raise */ });
    return () => { live = false; };
  }, []);

  const companyEntityId = railEntityId ?? resolvedId;

  const approvalReload = useAsyncRead(companyEntityId, (id) => getEntityApprovalMode(id));
  const pacing = useAsyncRead(companyEntityId, (id) => getPacing(id).then(unwrap));
  /**
   * ⚠️ THE ENTITY-SCOPED ROUTE, NOT THE ACCOUNT-WIDE ONE (P206 T0300).
   *
   * `/inboxkit/mailboxes` takes NO entity context and returns every mailbox the deployment owns —
   * the exact "global fallback" T0300 forbids, on a read any signed-in person can make. This row
   * read it, so a person's Connections could show sending identities belonging to another
   * workspace. `/mail/mailboxes?entity_id=` is the route that owns the question: it resolves the
   * mailbox's owner, refuses without entity access (`_require_entity_read`), and marks a mailbox
   * that no workspace owns yet as `shared` instead of implying it is the person's.
   */
  const mailboxes = useAsyncRead(companyEntityId, (id) =>
    listMailMailboxes(id).then((r) => unwrap(r).mailboxes ?? []),
  );
  const google = useAsyncRead(companyEntityId, (id) =>
    getGoogleIntegrationStatus(id).then(unwrap),
  );
  const linkedin = useAsyncRead(companyEntityId, (id) =>
    getLinkedInIntegrationStatus(id).then(unwrap),
  );
  const whatsapp = useAsyncRead(companyEntityId, (id) => getWhatsAppStatus(id).then(unwrap));
  const permissions = useAsyncRead(companyEntityId, (id) => listEntityPermissions(id));
  const person = useAsyncRead(resolvedId, (id) => getEntity(id).then(unwrap));
  /**
   * Row 5's PREFERENCE — the caller's own, keyed by the company in the rail. No uid is passed: the
   * route reads the caller from the token, so one person's answer can never be requested by another.
   */
  const attentionPreference = useAsyncRead(companyEntityId, (id) => getEntityAttention(id));
  /**
   * Row 9's count, from the ONE user-facing record of what was done.
   *
   * `POST /bot/call-history` is the audit trail a person may read (`validate_user_token`, not the
   * service-only memory route), it filters by entity, and its `pagination.total` is the number of
   * rows for that filter — a figure that RECONCILES to the store by construction, which is what
   * T0305 asks for. `pageSize: 1` because only the total is wanted; the rows themselves live on the
   * activity surface this row links to.
   */
  const receipts = useAsyncRead(companyEntityId, (id) =>
    getCallHistory({ entityId: id, pageSize: 1 }).then((r) => unwrap(r).pagination.total),
  );
  const round = useAsyncRead(companyEntityId, (id) => getRaiseProfile(id).then(unwrap));
  const plan = useAsyncRead("own", () => getMySubscription().then(unwrap));

  /**
   * Row 5's counts. `/harness/portfolio` is owner-scoped and returns the WHOLE company set, so this
   * read narrows it to the person's own company rather than summing every company he can see (a
   * figure that is not about this page's subject would be a wrong number, not a bigger one).
   */
  const attention = useAsyncRead("own", async () => {
    const r = await getPortfolio();
    const portfolio = unwrap(r);
    const mine = (portfolio.entities ?? []).find((e) => e.entity_id === companyEntityId);
    if (!mine) {
      return { process_gate: 0, hermes_wake: 0, preparation: null as number | null };
    }
    return {
      process_gate: mine.process_gate,
      hermes_wake: mine.hermes_wake,
      preparation: mine.preparation ?? null,
    };
  });

  const [approvalSave, setApprovalSave] = useState<SaveState>("idle");
  const [approvalSaveError, setApprovalSaveError] = useState<string | null>(null);
  const [pacingSave, setPacingSave] = useState<SaveState>("idle");
  const [pacingSaveError, setPacingSaveError] = useState<string | null>(null);
  const [attentionSave, setAttentionSave] = useState<SaveState>("idle");
  const [attentionSaveError, setAttentionSaveError] = useState<string | null>(null);

  /** A saved line is a moment, not a state — it clears itself so the row stops claiming news. */
  const flash = (set: (s: SaveState) => void) => {
    window.setTimeout(() => set("idle"), 4000);
  };

  const saveApprovalMode = useCallback(
    async (mode: EntityApprovalMode): Promise<SaveState> => {
      if (!companyEntityId) return "failed";
      setApprovalSave("saving");
      setApprovalSaveError(null);
      try {
        mustSucceed(await setEntityApprovalMode(companyEntityId, mode));
        setApprovalSave("saved");
        approvalReload.reload();
        flash(setApprovalSave);
        return "saved";
      } catch (err) {
        // The old value still stands on the BE — the section says so rather than pretending.
        setApprovalSave("failed");
        setApprovalSaveError(reasonOf(err));
        return "failed";
      }
    },
    [companyEntityId, approvalReload],
  );

  const savePacing = useCallback(
    async (preset: PacingPreset): Promise<SaveState> => {
      if (!companyEntityId) return "failed";
      setPacingSave("saving");
      setPacingSaveError(null);
      try {
        mustSucceed(await updatePacing(companyEntityId, { preset }));
        setPacingSave("saved");
        pacing.reload();
        flash(setPacingSave);
        return "saved";
      } catch (err) {
        setPacingSave("failed");
        setPacingSaveError(reasonOf(err));
        return "failed";
      }
    },
    [companyEntityId, pacing],
  );

  /**
   * Row 5's preference. An EMPTY list is sent as an empty list — "nothing may interrupt me" is an
   * answer a person is allowed to give, and treating it as "unset" would quietly restore the very
   * interruption he just switched off.
   */
  const saveAttention = useCallback(
    async (signals: AttentionSignal[]): Promise<SaveState> => {
      if (!companyEntityId) return "failed";
      setAttentionSave("saving");
      setAttentionSaveError(null);
      try {
        mustSucceed(await setEntityAttention(companyEntityId, signals));
        setAttentionSave("saved");
        attentionPreference.reload();
        flash(setAttentionSave);
        return "saved";
      } catch (err) {
        setAttentionSave("failed");
        setAttentionSaveError(reasonOf(err));
        return "failed";
      }
    },
    [companyEntityId, attentionPreference],
  );

  return {
    companyEntityId,
    approvalMode: approvalReload.read,
    pacing: pacing.read,
    mailboxes: mailboxes.read,
    google: google.read,
    linkedin: linkedin.read,
    whatsapp: whatsapp.read,
    needsYou: attention.read,
    attentionPreference: attentionPreference.read,
    permissions: permissions.read,
    person: person.read,
    round: round.read,
    receipts: receipts.read,
    plan: plan.read,
    saveApprovalMode,
    savePacing,
    saveAttention,
    approvalSave,
    approvalSaveError,
    pacingSave,
    pacingSaveError,
    attentionSave,
    attentionSaveError,
  };
}
